import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import { format, isToday, isYesterday, isSameDay } from 'date-fns';
import { fr } from 'date-fns/locale';
import EmojiPicker from 'emoji-picker-react';
import {
  FaPaperPlane,
  FaImage,
  FaSmile,
  FaEllipsisV,
  FaSearch,
  FaPhone,
  FaVideo,
  FaHeart,
  FaThumbtack,
  FaArchive,
  FaBan,
  FaFlag,
  FaBellSlash,
  FaBell,
  FaCheck,
  FaCheckDouble,
  FaReply,
  FaTrash,
  FaGift,
  FaLightbulb,
  FaCommentDots,
  FaArrowLeft,
  FaInfoCircle,
  FaLock,
  FaUnlock,
  FaGem,
  FaFire,
  FaPlus
} from 'react-icons/fa';


// Redux actions
import {
  fetchConversations,
  fetchMessages,
  sendMessage,
  createConversation,
  markMessagesAsRead,
  pinConversation,
  unpinConversation,
  archiveConversation,
  unarchiveConversation,
  addToFavorites,
  removeFromFavorites,
  blockUser,
  reportUser,
  setCurrentConversation,
  setDoNotDisturb,
  setTypingUser,
  addReaction,
  deleteMessage,
} from '../../store/slices/messagesSlice';

// Services
import socketService from '../../services/socket';
import api from '../../services/api';
import walletService from '../../services/walletService';
import giftService from '../../services/giftService';



// Utils
import { fixMediaUrl } from '../../utils/imageHelpers';


// Components
import { FloatingHeartsBackground, HeartButton } from '../../components/features/HeartAnimation';
import { TypingBubble } from '../../components/features/TypingIndicator';
import IcebreakerSuggestions from '../../components/features/IcebreakerSuggestions';
import QuickCompliments from '../../components/features/QuickCompliments';
import VirtualGift from '../../components/features/VirtualGift';
import FilterTabs from '../../components/features/FilterTabs';
import ConversationBadges from '../../components/features/ConversationBadges';
import RelationshipStatus from '../../components/features/RelationshipStatus';
import NewConversationModal from '../../components/features/NewConversationModal';

// Styles
import './Messages.css';


const Messages = () => {
  const dispatch = useDispatch();
  const {
    conversations,
    currentConversation,
    messages,
    loading,
    typingUsers,
    doNotDisturb,
    messageStatus,
    streaks,
  } = useSelector((state) => state.messages);
  const { user } = useSelector((state) => state.auth);

  // Local state
  const [messageInput, setMessageInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showIcebreakers, setShowIcebreakers] = useState(false);
  const [showCompliments, setShowCompliments] = useState(false);
  const [showGifts, setShowGifts] = useState(false);
  const [showConversationMenu, setShowConversationMenu] = useState(null);
  const [showMessageMenu, setShowMessageMenu] = useState(null);
  const [isTyping, setIsTyping] = useState(false);
  const [flirtMode, setFlirtMode] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [isMobileView, setIsMobileView] = useState(window.innerWidth <= 768);
  const [showConversationList, setShowConversationList] = useState(true);
  const [replyingTo, setReplyingTo] = useState(null);
  const [showRelationshipStatus, setShowRelationshipStatus] = useState(false);
  const [showNewConversation, setShowNewConversation] = useState(false);
  const [walletBalance, setWalletBalance] = useState(0);

  // Refs


  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const fileInputRef = useRef(null);

  // Fetch conversations and wallet balance on mount
  useEffect(() => {
    dispatch(fetchConversations());
    
    // Fetch wallet balance
    const fetchWalletBalance = async () => {
      try {
        const response = await walletService.getWallet();
        if (response.data?.success) {
          setWalletBalance(response.data.data?.balance || 0);
        }
      } catch (error) {
        console.error('Error fetching wallet balance:', error);
      }
    };
    fetchWalletBalance();
    
    const handleResize = () => {
      setIsMobileView(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [dispatch]);


  // Join conversation socket room when conversation changes
  useEffect(() => {
    if (currentConversation?.id) {
      socketService.joinConversation(currentConversation.id);
      
      // Fetch messages with error handling for 403
      dispatch(fetchMessages(currentConversation.id))
        .unwrap()
        .catch((error) => {
          console.error('Error fetching messages:', error);
          if (error?.message?.includes('Unauthorized') || error?.status === 403) {
            alert('Vous n\'avez pas accès à cette conversation');
            dispatch(setCurrentConversation(null));
          }
        });
      
      // Mark messages as read with error handling
      dispatch(markMessagesAsRead(currentConversation.id))
        .unwrap()
        .catch((error) => {
          console.error('Error marking messages as read:', error);
          // Silently ignore 403 errors for read status
        });
      
      if (isMobileView) {
        setShowConversationList(false);
      }
    }

    return () => {
      if (currentConversation?.id) {
        socketService.leaveConversation(currentConversation.id);
      }
    };
  }, [currentConversation?.id, dispatch, isMobileView]);



  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Typing indicator handler
  const handleTyping = useCallback(() => {
    if (!isTyping && currentConversation?.id) {
      setIsTyping(true);
      socketService.sendTyping(currentConversation.id, true);
    }

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    typingTimeoutRef.current = setTimeout(() => {
      setIsTyping(false);
      if (currentConversation?.id) {
        socketService.sendTyping(currentConversation.id, false);
      }
    }, 2000);
  }, [isTyping, currentConversation]);


  // Send message handler
  const handleSendMessage = async (e) => {
    e?.preventDefault();
    
    if (!messageInput.trim() || !currentConversation?.id) return;

    const content = messageInput.trim();
    setMessageInput('');
    setShowEmojiPicker(false);
    setReplyingTo(null);

    try {
      const result = await dispatch(sendMessage({
        conversationId: currentConversation.id,
        content,
        type: 'text',
      })).unwrap();
      
      // Emit socket event to broadcast message to other participants
      // result contains { message: {...}, diamant_info: {...} }
      socketService.sendMessage(result.message);
    } catch (error) {

      console.error('Failed to send message:', error);
    }
  };



  // Handle emoji selection
  const handleEmojiClick = (emojiData) => {
    setMessageInput((prev) => prev + emojiData.emoji);
    setShowEmojiPicker(false);
  };

  // Handle image upload
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !currentConversation?.id) return;

    const formData = new FormData();
    formData.append('image', file);
    try {
      await api.post(`/conversations/${currentConversation.id}/messages/upload`, formData);
      dispatch(fetchMessages(currentConversation.id));
    } catch (error) {
      console.error('Failed to upload image:', error);
    }
  };


  // Get other participant - handles multiple API response formats
  const getOtherParticipant = useCallback((conversation) => {
    if (!conversation || !user?.id) return null;
    
    const currentUserId = String(user.id);
    
    // Try different possible data structures from various API formats
    
    // 1. If participants array exists (most common format from backend)
    if (conversation.participants && Array.isArray(conversation.participants) && conversation.participants.length > 0) {
      // Convert IDs to strings for comparison to handle type mismatches
      const other = conversation.participants.find((p) => p && p.id && String(p.id) !== currentUserId);
      if (other) {
      // Normalize the participant data structure
        return {
          id: other.id,
          name: other.name || other.profile?.name || other.first_name || other.username || 'Utilisateur',
          avatar: other.avatar || other.profile?.avatar || other.photo || other.profile_photo || other.photo_url || other.profile?.photo_url || other.profile?.profile_picture || '/default-avatar.svg',
          profile: other.profile || null,
          is_online: other.is_online || false,
          ...other
        };

      }
      // If current user not in list, return first participant
      const first = conversation.participants[0];
      return {
        id: first.id,
        name: first.name || first.profile?.name || first.first_name || first.username || 'Utilisateur',
        avatar: first.avatar || first.profile?.avatar || first.photo || first.profile_photo || first.photo_url || first.profile?.photo_url || first.profile?.profile_picture || '/default-avatar.svg',
        profile: first.profile || null,
        is_online: first.is_online || false,
        ...first
      };

    }
    
    // 2. If participant object exists (single other user format)
    if (conversation.participant && typeof conversation.participant === 'object') {
      const p = conversation.participant;
      return {
        id: p.id,
        name: p.name || p.profile?.name || p.first_name || p.username || 'Utilisateur',
        avatar: p.avatar || p.profile?.avatar || p.photo || p.profile_photo || p.photo_url || p.profile?.photo_url || p.profile?.profile_picture || '/default-avatar.svg',
        profile: p.profile || null,
        is_online: p.is_online || false,
        ...p
      };
    }

    
    // 3. If other_user object exists (alternative format)
    if (conversation.other_user && typeof conversation.other_user === 'object') {
      const p = conversation.other_user;
      return {
        id: p.id,
        name: p.name || p.profile?.name || p.first_name || p.username || 'Utilisateur',
        avatar: p.avatar || p.profile?.avatar || p.photo || p.profile_photo || p.photo_url || p.profile?.photo_url || p.profile?.profile_picture || '/default-avatar.svg',
        profile: p.profile || null,
        is_online: p.is_online || false,
        ...p
      };
    }

    
    // 4. If user1_id and user2_id exist (minimal format)
    if (conversation.user1_id && conversation.user2_id) {
      const user1Id = String(conversation.user1_id);
      const user2Id = String(conversation.user2_id);
      const otherUserId = user1Id === currentUserId ? conversation.user2_id : conversation.user1_id;
      return {
        id: otherUserId,
        name: conversation.other_user_name || conversation.participant_name || 'Utilisateur',
        profile: conversation.other_user_profile || conversation.participant_profile || null,
        avatar: conversation.other_user_avatar || conversation.participant_avatar || conversation.other_user_photo_url || conversation.participant_photo_url || conversation.other_user_profile_picture || conversation.participant_profile_picture || '/default-avatar.svg',
      };
    }

    
    // 5. Check for user object directly on conversation
    if (conversation.user && typeof conversation.user === 'object' && String(conversation.user.id) !== currentUserId) {
      const p = conversation.user;
      return {
        id: p.id,
        name: p.name || p.profile?.name || p.first_name || p.username || 'Utilisateur',
        avatar: p.avatar || p.profile?.avatar || p.photo || p.profile_photo || p.photo_url || p.profile?.photo_url || p.profile?.profile_picture || '/default-avatar.svg',
        profile: p.profile || null,
        is_online: p.is_online || false,
        ...p
      };
    }

    
    // 6. Check for receiver/sender format
    if (conversation.receiver_id && String(conversation.receiver_id) !== currentUserId && conversation.receiver) {
      return conversation.receiver;
    }
    if (conversation.sender_id && String(conversation.sender_id) !== currentUserId && conversation.sender) {
      return conversation.sender;
    }
    
    return null;
  }, [user?.id]);




  // Filter conversations
  const filteredConversations = conversations.filter((conv) => {
    const otherParticipant = getOtherParticipant(conv);
    const participantName = otherParticipant?.profile?.name || otherParticipant?.name || '';
    const matchesSearch = !searchQuery || 
      participantName.toLowerCase().includes(searchQuery.toLowerCase());

    switch (activeFilter) {
      case 'favorites':
        return matchesSearch && conv.is_favorite;
      case 'unread':
        return matchesSearch && (conv.unread_count > 0);
      case 'matches':
        return matchesSearch && conv.is_match;
      case 'pinned':
        return matchesSearch && conv.is_pinned;
      case 'archived':
        return matchesSearch && conv.is_archived;
      default:
        return matchesSearch && !conv.is_archived;
    }
  });



  // Format message time
  const formatMessageTime = (date) => {
    if (!date) return '';
    const messageDate = new Date(date);
    // Check if date is valid
    if (isNaN(messageDate.getTime())) return '';
    if (isToday(messageDate)) {
      return format(messageDate, 'HH:mm');
    } else if (isYesterday(messageDate)) {
      return 'Hier';
    } else {
      return format(messageDate, 'dd/MM/yyyy');
    }
  };


  // Group messages by date
  const groupMessagesByDate = (messages) => {
    const groups = {};
    messages.forEach((message) => {
      // Skip messages with invalid or missing created_at
      if (!message.created_at) return;
      
      const date = new Date(message.created_at);
      // Check if date is valid
      if (isNaN(date.getTime())) return;
      
      const dateKey = isToday(date) ? 'Aujourd\'hui' : 
                      isYesterday(date) ? 'Hier' : 
                      format(date, 'dd MMMM yyyy', { locale: fr });
      
      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(message);
    });
    return groups;
  };


  // Handle conversation selection
  const handleConversationClick = (conversation) => {
    dispatch(setCurrentConversation(conversation));
  };

  // Handle back button (mobile)
  const handleBack = () => {
    dispatch(setCurrentConversation(null));
    setShowConversationList(true);
  };

  // Handle icebreaker selection
  const handleIcebreakerSelect = (text) => {
    setMessageInput(text);
    setShowIcebreakers(false);
  };

  // Handle compliment selection
  const handleComplimentSelect = (text) => {
    setMessageInput(text);
    setShowCompliments(false);
  };

  // Handle gift send
  const handleGiftSend = async (gift) => {
    if (!currentConversation?.id || !gift) return;
    
    try {
      // Get the other participant (receiver)
      const otherParticipant = getOtherParticipant(currentConversation);
      if (!otherParticipant?.id) {
        console.error('No receiver found for gift');
        return;
      }
      
      // Prevent sending gift to oneself
      if (String(otherParticipant.id) === String(user?.id)) {
        console.error('Cannot send gift to yourself');
        alert('Vous ne pouvez pas vous envoyer de cadeau à vous-même');
        return;
      }
      
      // Send gift via API
      const response = await giftService.sendGift(
        otherParticipant.id,
        gift.id,
        '', // message
        false // not anonymous
      );
      
      if (response.data?.success) {
        // Refresh wallet balance
        const walletRes = await walletService.getWallet();
        if (walletRes.data?.success) {
          setWalletBalance(walletRes.data.data?.balance || 0);
        }
        
        // Refresh messages to show the gift message
        if (currentConversation?.id) {
          dispatch(fetchMessages(currentConversation.id));
        }
        
        console.log('Gift sent successfully:', response.data);
      } else {

        console.error('Failed to send gift:', response.data?.message);
        alert(response.data?.message || 'Erreur lors de l\'envoi du cadeau');
      }
    } catch (error) {
      console.error('Error sending gift:', error);
      const errorMessage = error.response?.data?.message || 'Erreur lors de l\'envoi du cadeau';
      alert(errorMessage);
    } finally {
      setShowGifts(false);
    }
  };




  // Handle create new conversation
  const handleCreateConversation = async (userId) => {
    try {
      await dispatch(createConversation(userId)).unwrap();
      setShowNewConversation(false);
    } catch (error) {
      console.error('Failed to create conversation:', error);
    }
  };


  // Toggle flirt mode
  const toggleFlirtMode = () => {
    setFlirtMode(!flirtMode);
  };

  // Toggle do not disturb
  const toggleDoNotDisturb = () => {
    dispatch(setDoNotDisturb(!doNotDisturb));
  };

  // Handle message reaction
  const handleReaction = (messageId, reaction) => {
    dispatch(addReaction({ messageId, reaction: { emoji: reaction, user_id: user.id } }));
    socketService.sendReaction(messageId, reaction);
  };

  // Handle message delete
  const handleDeleteMessage = (messageId) => {
    dispatch(deleteMessage(messageId));
    setShowMessageMenu(null);
  };

  // Handle reply
  const handleReply = (message) => {
    setReplyingTo(message);
    setShowMessageMenu(null);
  };

  // Render conversation list
  const renderConversationList = () => (
    <div className={`conversations-sidebar ${!showConversationList && isMobileView ? 'hidden' : ''}`}>
      <div className="conversations-header">
        <h2>Messages</h2>
        <div className="header-actions">
          <button 
            className="action-btn"
            onClick={() => setShowNewConversation(true)}
            title="Nouvelle conversation"
          >
            <FaPlus />
          </button>
          <button 
            className={`action-btn ${doNotDisturb ? 'active' : ''}`}
            onClick={toggleDoNotDisturb}
            title={doNotDisturb ? 'Ne pas déranger activé' : 'Activer ne pas déranger'}
          >
            {doNotDisturb ? <FaBellSlash /> : <FaBell />}
          </button>
        </div>
      </div>


      <div className="search-container">
        <FaSearch className="search-icon" />
        <input
          type="text"
          placeholder="Rechercher une conversation..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="search-input"
        />
      </div>

      <FilterTabs
        activeFilter={activeFilter}
        onFilterChange={setActiveFilter}
        counts={{
          all: conversations.length,
          unread: conversations.filter(c => c.unread_count > 0).length,
          favorites: conversations.filter(c => c.is_favorite).length,
        }}
      />

      <div className="conversations-list">
        {loading ? (
          <div className="loading-conversations">
            <div className="spinner" />
            <p>Chargement...</p>
          </div>
        ) : conversations.length === 0 ? (
          <div className="empty-conversations">
            <FaCommentDots className="empty-icon" />
            <p>Aucune conversation</p>
            <span>Commencez à discuter avec quelqu'un !</span>
          </div>
        ) : filteredConversations.length === 0 ? (
          <div className="empty-conversations">
            <FaSearch className="empty-icon" />
            <p>Aucune conversation trouvée</p>
            <span>Essayez un autre filtre ou recherche</span>
          </div>
        ) : (
          filteredConversations.map((conversation, index) => {
            const otherParticipant = getOtherParticipant(conversation);
            
            // Simplified extraction - getOtherParticipant now normalizes data
            const participantName = otherParticipant?.name || 'Utilisateur';
            const participantAvatar = fixMediaUrl(otherParticipant?.avatar) || '/default-avatar.svg';

            
            const isActive = currentConversation?.id === conversation.id;


            const isTyping = typingUsers[conversation.id]?.length > 0;

            return (
              <motion.div
                key={conversation.id}
                className={`conversation-item ${isActive ? 'active' : ''} ${conversation.unread_count > 0 ? 'unread' : ''}`}
                onClick={() => handleConversationClick(conversation)}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                whileHover={{ scale: 1.02 }}
              >
                <div className="conversation-avatar">
                  <img
                    src={participantAvatar}
                    alt={participantName}
                    onError={(e) => { 
                      e.target.onerror = null;
                      e.target.src = '/default-avatar.svg'; 
                    }}
                  />

                  {otherParticipant?.is_online && <span className="online-indicator" />}
                </div>
                
                <div className="conversation-info">
                  <div className="conversation-header">
                    <h4>{participantName}</h4>

                    <span className="conversation-time">
                      {formatMessageTime(conversation.last_message?.created_at)}
                    </span>
                  </div>
                  
                  <div className="conversation-preview">
                    {isTyping ? (
                      <span className="typing-indicator">écrit...</span>
                    ) : (
                      <p>{conversation.last_message?.content || 'Nouvelle conversation'}</p>
                    )}
                  </div>
                  
                  <div className="conversation-meta">
                    {conversation.is_pinned && <FaThumbtack className="pinned-icon" />}
                    {conversation.is_favorite && <FaHeart className="favorite-icon" />}
                    {conversation.unread_count > 0 && (
                      <span className="unread-badge">{conversation.unread_count}</span>
                    )}
                    {streaks[conversation.id] > 0 && (
                      <span className="streak-badge">
                        <FaFire /> {streaks[conversation.id]}
                      </span>
                    )}
                  </div>
                </div>

                <button
                  className="conversation-menu-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowConversationMenu(showConversationMenu === conversation.id ? null : conversation.id);
                  }}
                >
                  <FaEllipsisV />
                </button>

                <AnimatePresence>
                  {showConversationMenu === conversation.id && (
                    <motion.div
                      className="conversation-menu"
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.9 }}
                    >
                      <button onClick={() => {
                        dispatch(conversation.is_pinned ? unpinConversation(conversation.id) : pinConversation(conversation.id));
                        setShowConversationMenu(null);
                      }}>
                        <FaThumbtack /> {conversation.is_pinned ? 'Désépingler' : 'Épingler'}
                      </button>
                      <button onClick={() => {
                        dispatch(conversation.is_favorite ? removeFromFavorites(conversation.id) : addToFavorites(conversation.id));
                        setShowConversationMenu(null);
                      }}>
                        <FaHeart /> {conversation.is_favorite ? 'Retirer des favoris' : 'Ajouter aux favoris'}
                      </button>
                      <button onClick={() => {
                        dispatch(conversation.is_archived ? unarchiveConversation(conversation.id) : archiveConversation(conversation.id));
                        setShowConversationMenu(null);
                      }}>
                        <FaArchive /> {conversation.is_archived ? 'Désarchiver' : 'Archiver'}
                      </button>
                      <button className="danger" onClick={() => {
                        dispatch(blockUser(otherParticipant?.id));
                        setShowConversationMenu(null);
                      }}>
                        <FaBan /> Bloquer
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })
        )}
      </div>
    </div>
  );

  // Render chat area
  const renderChatArea = () => {
    const otherParticipant = currentConversation ? getOtherParticipant(currentConversation) : null;
    const otherParticipantName = otherParticipant?.name || 'Utilisateur';
    const otherParticipantAvatar = fixMediaUrl(otherParticipant?.avatar) || '/default-avatar.svg';


    
    if (!currentConversation || !otherParticipant) {
      return (
        <div className="chat-area empty">
          <FloatingHeartsBackground />
          <div className="empty-chat-content">
            <FaCommentDots className="empty-icon" />
            <h3>Sélectionnez une conversation</h3>
            <p>Choisissez quelqu'un pour commencer à discuter</p>
          </div>
        </div>
      );
    }

    const isTypingInCurrent = typingUsers[currentConversation.id]?.length > 0;
    const groupedMessages = groupMessagesByDate(messages);

    return (
      <div className={`chat-area ${flirtMode ? 'flirt-mode' : ''} ${!showConversationList && isMobileView ? 'full' : ''}`}>
        {/* Chat Header */}
        <div className="chat-header">
          {isMobileView && (
            <button className="back-btn" onClick={handleBack}>
              <FaArrowLeft />
            </button>
          )}
          
          <div className="chat-header-info" onClick={() => setShowRelationshipStatus(!showRelationshipStatus)}>
            <div className="header-avatar">
              <img
                src={otherParticipantAvatar}
                alt={otherParticipantName}
                onError={(e) => { 
                  e.target.onerror = null;
                  e.target.src = '/default-avatar.svg'; 
                }}
              />

              {otherParticipant?.is_online && <span className="online-indicator" />}
            </div>
            
            <div className="header-details">
              <h4>{otherParticipantName}</h4>

              <span className="status-text">
                {isTypingInCurrent ? (
                  <span className="typing">écrit...</span>
                ) : otherParticipant?.is_online ? (
                  'En ligne'
                ) : (
                  otherParticipant?.last_seen || 'Hors ligne'
                )}
              </span>
            </div>
          </div>

          <div className="chat-header-actions">
            <HeartButton 
              active={flirtMode} 
              onClick={toggleFlirtMode} 
            />
            
            <button className="action-btn" onClick={() => setShowGifts(true)}>
              <FaGift />
            </button>
            
            <button className="action-btn" onClick={() => setShowRelationshipStatus(!showRelationshipStatus)}>
              <FaInfoCircle />
            </button>
            
            <button 
              className="action-btn danger"
              onClick={() => dispatch(reportUser({ userId: otherParticipant?.id, reason: 'inappropriate' }))}
            >
              <FaFlag />
            </button>
          </div>
        </div>

        {/* Relationship Status Panel */}
        <AnimatePresence>
          {showRelationshipStatus && (
            <motion.div
              className="relationship-panel"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
            >
              <RelationshipStatus 
                messageCount={messages.length}
                streakDays={streaks[currentConversation.id] || 0}
              />
              <ConversationBadges 
                badges={[
                  { type: 'newMatch', value: null },
                  { type: messages.length > 50 ? 'active' : null, value: null },
                  streaks[currentConversation.id] > 7 ? { type: 'streak', value: streaks[currentConversation.id] } : null,
                ].filter(Boolean)}
              />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Messages Area */}
        <div className="messages-container">
          {Object.entries(groupedMessages).map(([date, dateMessages]) => (
            <div key={date} className="message-group">
              <div className="date-separator">
                <span>{date}</span>
              </div>
              
              {dateMessages.map((message, index) => {
                const isOwn = message.sender_id === user?.id;
                const showAvatar = !isOwn && (index === 0 || dateMessages[index - 1]?.sender_id !== message.sender_id);
                
                return (
                  <motion.div
                    key={message.id}
                    className={`message ${isOwn ? 'own' : 'other'} ${message.is_read ? 'read' : 'unread'}`}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    layout
                  >
                    {!isOwn && showAvatar && (
                      <img
                        className="message-avatar"
                        src={otherParticipantAvatar}
                        alt=""
                        onError={(e) => { e.target.src = '/default-avatar.svg'; }}
                      />
                    )}

                    
                    <div className="message-content-wrapper">
                      <div 
                        className="message-bubble"
                        onContextMenu={(e) => {
                          e.preventDefault();
                          setShowMessageMenu(message.id);
                        }}
                      >
                        {message.reply_to && (
                          <div className="reply-reference">
                            <span>Réponse à: {message.reply_to.content?.substring(0, 50)}...</span>
                          </div>
                        )}
                        
                        {message.message_type === 'image' ? (
                          <img 
                            src={message.media_file} 
                            alt="Shared" 
                            className="message-image"
                            onClick={() => setSelectedImage(message.media_file)}
                          />
                        ) : message.message_type === 'gift' ? (
                          <div className="gift-message">
                            <div className="gift-icon-large">🎁</div>
                            <p className="message-text">
                              {message.sender_id === user?.id 
                                ? message.text.replace(/^🎁 .+ vous a envoyé un cadeau :/, '🎁 Vous avez envoyé un cadeau :')
                                : message.text
                              }
                            </p>
                          </div>
                        ) : (
                          <p className="message-text">{message.text}</p>
                        )}


                        
                        <div className="message-meta">
                          <span className="message-time">
                            {formatMessageTime(message.created_at)}
                          </span>
                          {isOwn && (
                            <span className="read-status">
                              {message.is_read ? <FaCheckDouble className="read" /> : <FaCheck />}
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {/* Reactions */}
                      {message.reactions?.length > 0 && (
                        <div className="message-reactions">
                          {message.reactions.map((reaction, idx) => (
                            <span key={idx} className="reaction">{reaction.emoji}</span>
                          ))}
                        </div>
                      )}
                      
                      {/* Message Menu */}
                      <AnimatePresence>
                        {showMessageMenu === message.id && (
                          <motion.div
                            className="message-menu"
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.9 }}
                          >
                            <div className="reaction-picker">
                              {['❤️', '😂', '👍', '😮', '😢', '🔥'].map(emoji => (
                                <button 
                                  key={emoji} 
                                  onClick={() => handleReaction(message.id, emoji)}
                                >
                                  {emoji}
                                </button>
                              ))}
                            </div>
                            <button onClick={() => handleReply(message)}>
                              <FaReply /> Répondre
                            </button>
                            {isOwn && (
                              <button onClick={() => handleDeleteMessage(message.id)}>
                                <FaTrash /> Supprimer
                              </button>
                            )}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          ))}
          
          {isTypingInCurrent && (
            <div className="typing-indicator-container">
              <TypingBubble />
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <div className="chat-input-area">
          {/* Reply Preview */}
          {replyingTo && (
            <div className="reply-preview">
              <span>Réponse à: {replyingTo.text?.substring(0, 50)}...</span>
              <button onClick={() => setReplyingTo(null)}>×</button>
            </div>
          )}

          {/* Feature Panels */}
          <AnimatePresence>
            {showIcebreakers && (
              <IcebreakerSuggestions 
                onSelect={handleIcebreakerSelect} 
                isVisible={showIcebreakers} 
              />
            )}
            {showCompliments && (
              <QuickCompliments 
                onSelect={handleComplimentSelect} 
                isVisible={showCompliments} 
              />
            )}
            {showGifts && (
              <VirtualGift 
                onSend={handleGiftSend} 
                onClose={() => setShowGifts(false)}
                isVisible={showGifts}
                userBalance={walletBalance}
              />
            )}


          </AnimatePresence>

          <form className="input-form" onSubmit={handleSendMessage}>
            <div className="input-actions">
              <button
                type="button"
                className="action-btn"
                onClick={() => setShowIcebreakers(!showIcebreakers)}
                title="Cassez la glace"
              >
                <FaLightbulb />
              </button>
              
              <button
                type="button"
                className="action-btn"
                onClick={() => setShowCompliments(!showCompliments)}
                title="Compliments rapides"
              >
                <FaHeart />
              </button>
              
              <button
                type="button"
                className="action-btn"
                onClick={() => fileInputRef.current?.click()}
                title="Envoyer une image"
              >
                <FaImage />
              </button>
              
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleImageUpload}
                accept="image/*"
                style={{ display: 'none' }}
              />
            </div>

            <div className="message-input-wrapper">
              <button
                type="button"
                className="emoji-btn"
                onClick={() => setShowEmojiPicker(!showEmojiPicker)}
              >
                <FaSmile />
              </button>
              
              <input
                type="text"
                value={messageInput}
                onChange={(e) => {
                  setMessageInput(e.target.value);
                  handleTyping();
                }}
                placeholder={flirtMode ? "Mode flirt activé 💕 Soyez romantique..." : "Écrivez votre message..."}
                className="message-input"
              />
              
              {showEmojiPicker && (
                <div className="emoji-picker-container">
                  <EmojiPicker onEmojiClick={handleEmojiClick} />
                </div>
              )}
            </div>

            <button
              type="submit"
              className={`send-btn ${flirtMode ? 'flirt' : ''}`}
              disabled={!messageInput.trim()}
            >
              <FaPaperPlane />
            </button>
          </form>

          {flirtMode && (
            <div className="flirt-mode-indicator">
              <span>💕 Mode Flirt activé - Soyez romantique !</span>
            </div>
          )}
        </div>

        {/* Image Modal */}
        <AnimatePresence>
          {selectedImage && (
            <motion.div
              className="image-modal"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelectedImage(null)}
            >
              <img src={selectedImage} alt="Full size" />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  return (
    <div className="messages-page">
      <FloatingHeartsBackground />
      {renderConversationList()}
      {renderChatArea()}
      
      <NewConversationModal
        isOpen={showNewConversation}
        onClose={() => setShowNewConversation(false)}
        onCreateConversation={handleCreateConversation}
        existingConversations={conversations}
      />
    </div>
  );

};

export default Messages;
